import * as React from 'react'
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import type {
  McpSettingDto,
  McpStatusDto,
  MyConnectionListDto,
  OrgConnectionListDto,
  RecognizedAppDto,
  RecognizedAppListDto,
  SeenAppListDto,
  StaffConnectionListDto,
} from '@lib/dto/mcp.dto'
import type { McpOrgSetting } from '@lib/enums/mcp-org-setting.enum'
import type { AddRecognizedAppInput } from '@lib/input/mcp.input'
import type { Uuid } from '@lib/validators/params'
import { useDashboardScope } from '@/dashboard/DashboardScope'
import { useRefreshOnForbidden } from '@/dashboard/useRefreshOnForbidden'
import { publicApi } from '@/helpers/apiClient'
import { mcpKeys, teamKeys } from '@/hooks/queryKeys'
import { ConnectedAppsService } from '@/services/ConnectedAppsService'
import { opsMcpService } from '@/services/OpsMcpService'

const publicService = new ConnectedAppsService(publicApi)

/** Whether MCP is switched on; the dashboard shows its pages and settings only then. */
export function useMcpStatus(): UseQueryResult<McpStatusDto> {
  return useQuery({ queryKey: mcpKeys.status(), queryFn: () => publicService.status(), staleTime: 5 * 60_000 })
}

export function useMcpEnabled(): boolean {
  return useMcpStatus().data?.enabled === true
}

/** The connected-apps service bound to the current dashboard session (tenant user or staff view-as). */
function useScopedService(): ConnectedAppsService {
  const { client } = useDashboardScope()
  return React.useMemo(() => new ConnectedAppsService(client), [client])
}

/** The signed-in person's own connections (not shown to staff viewing as the organization). */
export function useMyConnections(options: { enabled: boolean }): UseQueryResult<MyConnectionListDto> {
  const service = useScopedService()
  return useQuery({ queryKey: mcpKeys.mine(), queryFn: () => service.mine(), enabled: options.enabled })
}

/** The members' connections reaching this organization (Owners, and staff viewing as it). */
export function useOrgConnections(options: { enabled: boolean }): UseQueryResult<OrgConnectionListDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedService()
  return useQuery({ queryKey: mcpKeys.org(tenantId, cacheKey), queryFn: () => service.orgConnections(tenantId), enabled: options.enabled })
}

export function useMcpSetting(options: { enabled: boolean }): UseQueryResult<McpSettingDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedService()
  return useQuery({ queryKey: mcpKeys.setting(tenantId, cacheKey), queryFn: () => service.setting(tenantId), enabled: options.enabled })
}

/**
 * Ending connections and changing the MCP setting. Each refreshes the
 * connection lists, the setting, and the organization's activity.
 */
export function useConnectedAppsActions(): {
  disconnect: UseMutationResult<void, Error, Uuid>
  removeFromOrg: UseMutationResult<void, Error, Uuid>
  updateSetting: UseMutationResult<McpSettingDto, Error, McpOrgSetting>
} {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedService()
  const queryClient = useQueryClient()
  const onError = useRefreshOnForbidden()
  const onSettled = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: mcpKeys.all }),
      queryClient.invalidateQueries({ queryKey: teamKeys.activity(tenantId, cacheKey) }),
    ])
  }
  return {
    disconnect: useMutation({ mutationFn: (connectionId) => service.disconnect(connectionId), onError, onSettled }),
    removeFromOrg: useMutation({ mutationFn: (connectionId) => service.removeFromOrg(tenantId, connectionId), onError, onSettled }),
    updateSetting: useMutation({ mutationFn: (setting) => service.updateSetting(tenantId, { setting }), onError, onSettled }),
  }
}

/** Staff connections on the ops console: the signed-in staff member's own, or everyone's for admins. */
export function useStaffConnections(options: { enabled: boolean }): UseQueryResult<StaffConnectionListDto> {
  return useQuery({ queryKey: mcpKeys.ops.connections(), queryFn: () => opsMcpService.connections(), enabled: options.enabled })
}

/** The AI apps PixelSign recognizes for live changes and staff changes. */
export function useRecognizedApps(options: { enabled: boolean }): UseQueryResult<RecognizedAppListDto> {
  return useQuery({ queryKey: mcpKeys.ops.recognized(), queryFn: () => opsMcpService.recognizedApps(), enabled: options.enabled })
}

/** Apps people signed in with lately, by exactly how each identifies itself (for deciding what to recognize). */
export function useSeenApps(options: { enabled: boolean }): UseQueryResult<SeenAppListDto> {
  return useQuery({ queryKey: mcpKeys.ops.seen(), queryFn: () => opsMcpService.seenApps(), enabled: options.enabled })
}

/** Ending staff connections and managing recognized apps. Each refreshes the ops MCP views. */
export function useOpsMcpActions(): {
  disconnect: UseMutationResult<void, Error, Uuid>
  addRecognizedApp: UseMutationResult<RecognizedAppDto, Error, AddRecognizedAppInput>
  removeRecognizedApp: UseMutationResult<void, Error, Uuid>
} {
  const queryClient = useQueryClient()
  const onSettled = (): Promise<void> => queryClient.invalidateQueries({ queryKey: mcpKeys.ops.all() })
  return {
    disconnect: useMutation({ mutationFn: (connectionId) => opsMcpService.disconnect(connectionId), onSettled }),
    addRecognizedApp: useMutation({ mutationFn: (input) => opsMcpService.addRecognizedApp(input), onSettled }),
    removeRecognizedApp: useMutation({ mutationFn: (appId) => opsMcpService.removeRecognizedApp(appId), onSettled }),
  }
}
